// Core modules
import { Component, Input, OnChanges, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, UntypedFormControl, AbstractControl, Validators } from '@angular/forms';
import { AuthenticationService } from '../../../../services/authentication.service';
import { SurvaleCommonComponent } from '../../../../common/survale-common.component';
import { ClientService } from '@services/client.service';
import { ToastrService } from 'ngx-toastr';
import { firstValueFrom, lastValueFrom } from 'rxjs';
import { ConfigService } from '@services/config.service';
import * as X2JS from 'x2js';
import * as _ from 'lodash';
import { GeneralDataService } from '@services/general-data-service';

/**
 * SSO Settings component
 */
@Component({
    selector: 'app-sso-settings',
    templateUrl: './sso-settings.component.html',
    styleUrls: ['./sso-settings.component.scss'],
    standalone: false
})
export class SsoSettingsComponent extends SurvaleCommonComponent implements OnInit {
  @Input() settings: any;

  addSSOForm: UntypedFormGroup;
  dummyForm: UntypedFormGroup;
  logMessageOpts = ['yes', 'no'];
  authProvider = { provider: '', metaDataUrl: '' };
  selectedFile: File | null = null;
  fileName = '';
  isSamlIdentifierValid = true;

  /**
   * Life cycle method
   */
  constructor(
    public authenticationService: AuthenticationService,
    public generalDataService: GeneralDataService,
    private clientService: ClientService,
    private toastr: ToastrService,
    private fb: UntypedFormBuilder,
  ) {
    super(authenticationService);
  }

  get authName(): AbstractControl {
    return this.addSSOForm.get('authName');
  }

  get authTitle(): AbstractControl {
    return this.addSSOForm.get('authTitle');
  }

  get logMessages(): AbstractControl {
    return this.addSSOForm.get('logMessages');
  }

  get entryPoint(): AbstractControl {
    return this.addSSOForm.get('entryPoint');
  }

  get issuer(): AbstractControl {
    return this.addSSOForm.get('issuer');
  }

  get audience(): AbstractControl {
    return this.addSSOForm.get('audience');
  }

  get cert(): AbstractControl {
    return this.addSSOForm.get('cert');
  }

  get apiHost(): string {
    return ConfigService.config.apiHost;
  }

  get metaDataUrl(): AbstractControl {
    return this.dummyForm.get('metaDataUrl');
  }

  get provider(): AbstractControl {
    return this.dummyForm.get('provider');
  }

  /**
   * Life cycle init method
   */
  ngOnInit(): void {
    this.addSSOForm = this.fb.group({
      authName: new UntypedFormControl('', Validators.required),
      authTitle: new UntypedFormControl('', Validators.required),
      logMessages: new UntypedFormControl('yes', Validators.required),
      entryPoint: new UntypedFormControl('', Validators.required),
      issuer: new UntypedFormControl('', Validators.required),
      audience: new UntypedFormControl(''),
      cert: new UntypedFormControl('', Validators.required),
    });
    this.dummyForm = this.fb.group({
      provider: new UntypedFormControl('', Validators.required),
      metaDataUrl: new UntypedFormControl('', Validators.required),
    });

    this.getSSO();
  }

  async getSSO(): Promise<void> {
    try {
      const ssoResp: any = await firstValueFrom(this.clientService.getSSO());
      if (ssoResp?.body) {
        this.addSSOForm.patchValue(ssoResp.body);
      }
    } catch (error) {
      console.error('Error loading SSO settings:', error);
    }
  }

  validateSamlIdentifier(): void {
    const value = this.addSSOForm.value.authName;
    this.isSamlIdentifierValid = !_.isNull(value.match(/[a-zA-Z0-9\-\._~]/g)) && value.length === value.match(/[a-zA-Z0-9\-\._~]/g).length;
  }

  samlIdentifierChanged(): void {
    const v = this.addSSOForm.value.authName;
    this.addSSOForm.patchValue({ issuer: `${this.apiHost}/v0/auth/${v}` });
  }

  async saveSSO(): Promise<void> {
    try {
      await firstValueFrom(this.clientService.setSSO(this.addSSOForm.value));
      this.toastr.success('SSO settings saved successfully');
    } catch (error) {
      console.error('Error saving SSO:', error);
      this.toastr.error('Error saving SSO settings');
    }
  }

  async processSSO(): Promise<void> {
    try {
      if (this.authProvider.provider === 'okta') {
        // const response = await fetch(this.authProvider.metaDataUrl.trim());
        const response = await firstValueFrom(this.clientService.fetchURLContent(this.authProvider.metaDataUrl.trim()));
        const xml: string = response.body.data;
        const x2js = new X2JS();
        const doc = x2js.xml2js(xml);
        this.addSSOForm.patchValue({
          cert: _.get(doc, 'EntityDescriptor.IDPSSODescriptor.KeyDescriptor.KeyInfo.X509Data.X509Certificate.__text'),
          entryPoint: _.get(doc, 'EntityDescriptor.IDPSSODescriptor.SingleSignOnService[0]._Location'),
          issuer: _.get(doc, 'EntityDescriptor.IDPSSODescriptor.SingleSignOnService[0]._Location'),
        });
      } else if (this.authProvider.provider === 'azure') {
        const response: any = await lastValueFrom(this.generalDataService.reflectResponse(this.authProvider.metaDataUrl.trim()));
        const xml = response.body.data;
        const x2js = new X2JS();
        const doc = x2js.xml2js(xml);
        this.addSSOForm.patchValue({
          cert: _.get(doc, 'EntityDescriptor.IDPSSODescriptor.KeyDescriptor.KeyInfo.X509Data.X509Certificate'),
          entryPoint: _.get(doc, 'EntityDescriptor.IDPSSODescriptor.SingleSignOnService[0]._Location'),
        });
      }
      this.toastr.success('SSO information extracted successfully');
    } catch (error) {
      this.toastr.error('Error extracting SSO: ' + error.toString());
      console.error('Error processing SSO:', error);
    }
  }

  /**
   * Handle file selection for XML upload
   */
  onFileSelected(event: any): void {
    const file = event.target.files[0];
    if (file) {
      if (file.type === 'text/xml' || file.name.endsWith('.xml')) {
        this.selectedFile = file;
        this.fileName = file.name;
        this.toastr.success(`File "${file.name}" selected successfully`);
      } else {
        this.toastr.error('Please select a valid XML file');
        event.target.value = '';
      }
    }
  }

  /**
   * Process uploaded XML file and extract SAML information
   */
  async processUploadedXML(): Promise<void> {
    if (!this.selectedFile) {
      this.toastr.error('Please select an XML file first');
      return;
    }

    try {
      const xmlContent = await this.readFileAsText(this.selectedFile);
      const x2js = new X2JS();
      const doc = x2js.xml2js(xmlContent);

      const extractedData = this.extractSAMLData(doc);

      if (extractedData) {
        this.addSSOForm.patchValue(_.omit(extractedData, ['authName', 'authTitle', 'logMessages']));
        this.toastr.success('SAML information extracted successfully from XML file');
      } else {
        this.toastr.error('Could not extract SAML information from the XML file');
      }
    } catch (error) {
      console.error('Error processing XML file:', error);
      this.toastr.error('Error processing XML file: ' + error.toString());
    }
  }

  /**
   * Trigger file input click
   */
  triggerFileInput(): void {
    const fileInput = document.getElementById('xmlFileInput') as HTMLInputElement;
    if (fileInput) {
      fileInput.click();
    }
  }

  /**
   * Clear selected file
   */
  clearSelectedFile(): void {
    this.selectedFile = null;
    this.fileName = '';
    const fileInput = document.getElementById('xmlFileInput') as HTMLInputElement;
    if (fileInput) {
      fileInput.value = '';
    }
  }

  /**
   * Read file as text
   */
  private readFileAsText(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const content = e.target?.result as string;
        resolve(content);
      };
      reader.onerror = (e) => reject(e);
      reader.readAsText(file);
    });
  }

  /**
   * Extract SAML data from parsed XML document
   */
  private extractSAMLData(doc: any): any {
    try {
      const idpSSODescriptor = _.get(doc, 'EntityDescriptor.IDPSSODescriptor');

      if (idpSSODescriptor) {
        let cert = '';
        const keyDescriptor = _.get(doc, 'EntityDescriptor.IDPSSODescriptor.KeyDescriptor');
        if (keyDescriptor) {
          if (Array.isArray(keyDescriptor)) {
            for (const keyDesc of keyDescriptor) {
              const certFromKey = _.get(keyDesc, 'KeyInfo.X509Data.X509Certificate.__text') ||
                _.get(keyDesc, 'KeyInfo.X509Data.X509Certificate');
              if (certFromKey) {
                cert = certFromKey;
                break;
              }
            }
          } else {
            cert = _.get(keyDescriptor, 'KeyInfo.X509Data.X509Certificate.__text') ||
              _.get(keyDescriptor, 'KeyInfo.X509Data.X509Certificate');
          }
        }

        if (!cert) {
          cert = _.get(doc, 'EntityDescriptor.IDPSSODescriptor.KeyDescriptor[0].KeyInfo.X509Data.X509Certificate.__text') ||
            _.get(doc, 'EntityDescriptor.IDPSSODescriptor.KeyDescriptor[0].KeyInfo.X509Data.X509Certificate');
        }

        let entryPoint = '';
        const singleSignOnServices = _.get(doc, 'EntityDescriptor.IDPSSODescriptor.SingleSignOnService');

        if (singleSignOnServices && Array.isArray(singleSignOnServices)) {
          const postService = singleSignOnServices.find((service: any) =>
            service._Binding === 'urn:oasis:names:tc:SAML:2.0:bindings:HTTP-POST'
          );
          const redirectService = singleSignOnServices.find((service: any) =>
            service._Binding === 'urn:oasis:names:tc:SAML:2.0:bindings:HTTP-Redirect'
          );
          entryPoint = postService?._Location || redirectService?._Location || singleSignOnServices[0]?._Location || '';
        } else if (singleSignOnServices) {
          entryPoint = singleSignOnServices._Location || '';
        }

        const issuer = _.get(doc, 'EntityDescriptor._entityID') || entryPoint;
        const entityID = _.get(doc, 'EntityDescriptor._entityID', '');
        let authName = '';
        let authTitle = '';

        if (entityID) {
          try {
            const url = new URL(entityID);
            const domain = url.hostname.split('.')[0];
            authName = domain.toLowerCase() + '_saml';
            authTitle = domain.charAt(0).toUpperCase() + domain.slice(1) + ' SAML';
          } catch (e) {
            authName = 'company_saml';
            authTitle = 'Company SAML';
          }
        }

        return {
          cert: cert || '',
          entryPoint: entryPoint || '',
          issuer: issuer || '',
          audience: entityID || '',
          authName: authName || '',
          authTitle: authTitle || ''
        };
      }

      const keyDescriptors = _.get(doc, 'EntityDescriptor.KeyDescriptor');
      if (keyDescriptors) {
        const keyDescriptor = Array.isArray(keyDescriptors) ? keyDescriptors[0] : keyDescriptors;
        const cert = _.get(keyDescriptor, 'KeyInfo.X509Data.X509Certificate.__text') ||
          _.get(keyDescriptor, 'KeyInfo.X509Data.X509Certificate');

        return {
          cert: cert || '',
          entryPoint: '',
          issuer: _.get(doc, 'EntityDescriptor._entityID') || '',
          audience: _.get(doc, 'EntityDescriptor._entityID') || '',
          authName: '',
          authTitle: ''
        };
      }

      return null;
    } catch (error) {
      console.error('Error extracting SAML data:', error);
      return null;
    }
  }
}
